import React, { useEffect } from 'react';
import ImageThumbnail from '../new-game-inputs/ImageThumbnail';
import NewGameState from '../_types/NewGameState';
import GameImage from '../new-game-inputs/GameImage';
import BracketContestants from '../new-game-inputs/BracketContestants/seed/BracketSeedContestants.container';
import {
  ImageSection,
  ImageUploadContainer,
  AddImageButton,
  AddImageText,
  ImagePreviewContainer,
  ImagePreview,
  ImageName,
  renderGamifyIcon,
  CustomInputLabel,
} from '../GameFormComponents';
import AddContestantsView from '../new-game-inputs/AddContestants/AddContestants.view';

interface Props {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
}

// Helper function to preprocess team data
const preprocessTeamData = (gameData: any) => {
  if (!gameData || !gameData.id || gameData.scope !== 'circle') {
    return gameData;
  }

  // Only process if competitors is empty but we have user data
  if (
    (!gameData.competitors || gameData.competitors.length === 0) &&
    gameData.users?.items?.length > 0
  ) {
    // Track existing team IDs and data
    const teamIds: string[] = [];
    const teamData: any[] = [];

    // Process each user item to extract team info
    gameData.users.items.forEach((item: any) => {
      if (item?.circleID && item?.circle?.name) {
        // Check if we've already processed this team
        if (!teamIds.includes(item.circleID)) {
          teamIds.push(item.circleID);
          teamData.push({
            id: item.circleID,
            value: item.circleID,
            name: item.circle.name,
            avatar: '',
          });
        }
      }
    });

    if (teamIds.length > 0) {
      return {
        ...gameData,
        assigneeIDs: teamIds,
        competitors: teamData,
        assigneeNames: teamData.map((t: any) => t.name),
      };
    }
  }

  return gameData;
};

const Step3: React.FC<Props> = ({ newGameState, onChangeNewGameState }) => {
  // Preprocess team data if needed
  const processedGameState = preprocessTeamData(newGameState as any);

  // Log if we've made any changes
  useEffect(() => {
    if (processedGameState !== newGameState) {
      // Update the parent state with our processed data
      onChangeNewGameState(processedGameState);
    }
  }, []);

  // Add debug logging to check the state values
  // Show bracket contestants if we have totalCompetitors defined and greater than 0
  const shouldShowBracketContestants =
    processedGameState.bracket &&
    processedGameState.bracket.totalCompetitors > 0;

  const triggerImageUpload = () => {
    const imageInput = document.querySelector(
      'input[type="file"][accept="image/*"]'
    );
    if (imageInput) {
      (imageInput as HTMLElement).click();
    }
  };

  if (
    shouldShowBracketContestants &&
    processedGameState.contestType.includes('bracket')
  ) {
    return (
      <>
        <BracketContestants
          newGameState={processedGameState}
          onChangeNewGameState={onChangeNewGameState}
        />
        <GameImage
          newGameState={processedGameState}
          onChangeNewGameState={onChangeNewGameState}
        />
      </>
    );
  }

  const renderImageSection = () => (
    <ImageSection>
      <CustomInputLabel>Image/thumbnail</CustomInputLabel>

      <ImageUploadContainer>
        <AddImageButton onClick={triggerImageUpload}>
          {renderGamifyIcon('add', 'white', 16)}
          <AddImageText>Add</AddImageText>
        </AddImageButton>

        {processedGameState.imageFile && (
          <ImagePreviewContainer>
            <ImagePreview
              src={URL.createObjectURL(processedGameState.imageFile)}
              alt="Preview"
            />
            <ImageName>{processedGameState.imageName}</ImageName>
          </ImagePreviewContainer>
        )}
      </ImageUploadContainer>

      <div style={{ display: 'none' }}>
        <ImageThumbnail
          newGameState={processedGameState}
          onChangeNewGameState={onChangeNewGameState}
        />
      </div>
    </ImageSection>
  );

  return (
    <>
      <AddContestantsView
        newGameState={processedGameState}
        onChangeNewGameState={onChangeNewGameState}
      />

      {renderImageSection()}
    </>
  );
};

export default Step3;
